"use client";

/**
 * OutdatedBrowserBanner — thin top-of-page notice for visitors on old browser
 * engines (e.g. Safari 13 on macOS 10.13 High Sierra, the last version Apple
 * ever shipped there) where parts of the app may not render correctly.
 * Detection is feature-based (see useOutdatedBrowser), not user-agent
 * sniffing. Dismissal is remembered in localStorage.
 */

import { useTranslations } from "next-intl";
import { X, AlertTriangle } from "lucide-react";
import { useOutdatedBrowser } from "@/hooks/use-outdated-browser";

export function OutdatedBrowserBanner() {
  const t = useTranslations("common");
  const { show, dismiss } = useOutdatedBrowser();

  if (!show) return null;

  return (
    <div className="flex items-center gap-3 w-full bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-xs">
      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
      <span className="text-foreground flex-1">{t("outdatedBrowserMessage")}</span>
      <a
        href="https://browsehappy.com/"
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground text-background text-xs font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
      >
        {t("outdatedBrowserUpdate")}
      </a>
      <button
        onClick={dismiss}
        aria-label={t("outdatedBrowserDismiss")}
        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
